import { AxiosResponse, AxiosError } from "axios";
import apiClient from './ApiConfig';

export interface User {
    id: number;
    first_name: string;
    last_name: string;
    init_num: number;
    mit_alum_id: string;
    year: number;
    chapter: string;
    pledge_class_id: number;
    pledge_class?: PledgeClass;
    big_brother_user_id: number;
    family_id: number;
    family?: Family;
    privacy_setting: string;
    is_corporation_member: boolean;
    company: string;
    title: string;
    email: string;
    type: string;
    send_monthly_email: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    home_phone: string;
    work_phone: string;
    cell_phone: string;
    web_page: string;
    notes: string;
    admin_notes: string;
    alternate_email_1: string;
    alternate_email_2: string;
    enabled: string;
    missing_email: boolean;
    missing_address: boolean;
}

export interface UserHistory {
    id: number;
    user_id: number;
    text: string;
    time: string;
    modified_by_user: {
        id: number;
        first_name: string;
        last_name: string;
    };
}

export interface UserResponse {
    result: string;
    user: User;
    canEdit?: boolean;
    hasAdminPermissions?: boolean;
}

export interface UsersResponse {
    result: string;
    users: User[];
    canCreate?: boolean;
}

export interface UserHistoryResponse {
    result: string;
    history: UserHistory[];
}

export interface PledgeClass {
    id: number;
    name: string;
    pledge_year: number;
    pledge_term: string;
    graduation_year: number;
}

export interface Family {
    id: number;
    name: string;
}

export interface UserCreateRequest {
    first_name: string;
    last_name: string;
    init_num: number;
    mit_alum_id: string;
    year: number;
    chapter: string;
    pledge_class_id: number;
    big_brother_user_id: number;
    family_id: number;
    privacy_setting: string;
    is_corporation_member: boolean;
    company: string;
    title: string;
    email: string;
    type: string;
    send_monthly_email: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    home_phone: string;
    work_phone: string;
    cell_phone: string;
    web_page: string;
    notes: string;
    admin_notes: string;
    alternate_email_1: string;
    alternate_email_2: string;
}

export interface UserUpdateRequest extends UserCreateRequest {
    id: number;
}

export interface UserSearchRequest {
    first_name?: string;
    last_name?: string;
    year?: number;
    pledge_class_id?: number;
    family_id?: number;
    is_corporation_member?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    company?: string;
    title?: string;
    email?: string;
    notes?: string;
}

// Get all pledge classes
export function GetPledgeClasses(
    callback: (statusCode: number, pledgeClasses: PledgeClass[]) => void
) {
    apiClient
        .get("/pledge-classes")
        .then((response: AxiosResponse) => {
            if (
                response.data?.result === "success" &&
                Array.isArray(response.data?.pledge_classes)
            ) {
                callback(200, response.data.pledge_classes);
            } else {
                callback(500, []);
            }
        })
        .catch((error: AxiosError) => {
            console.error("Error fetching pledge classes:", error);
            callback(error.response?.status || 500, []);
        });
}

// Get all families
export function GetFamilies(
    callback: (statusCode: number, families: Family[]) => void
) {
    apiClient
        .get("/families")
        .then((response: AxiosResponse) => {
            if (response.data.result === "success") {
                callback(200, response.data.families);
            } else {
                callback(500, []);
            }
        })
        .catch((error: AxiosError) => {
            console.error("Error fetching families:", error);
            callback(error.response?.status || 500, []);
        });
}

// Get all users for list view
export function GetUsers(
    callback: (statusCode: number, users: User[], canCreate?: boolean) => void
) {
    apiClient
        .get("/users")
        .then((response: AxiosResponse<UsersResponse>) => {
            if (response.data.result === "success") {
                callback(200, response.data.users, response.data.canCreate);
            } else {
                callback(500, [], false);
            }
        })
        .catch((error: AxiosError) => {
            console.error("Error fetching users:", error);
            callback(error.response?.status || 500, [], false);
        });
}

// Get users for dropdown (minimal data)
export function GetUsersForDropdown(
    callback: (statusCode: number, users: User[]) => void
) {
    apiClient
        .get("/users?use=dropdown")
        .then((response: AxiosResponse) => {
            if (response.data.result === "success") {
                callback(200, response.data.users);
            } else {
                callback(500, []);
            }
        })
        .catch((error: AxiosError) => {
            console.error("Error fetching users for dropdown:", error);
            callback(error.response?.status || 500, []);
        });
}

// Get a single user by ID
export function GetUser(
    id: number,
    callback: (statusCode: number, user: User | null, canEdit?: boolean, hasAdminPermissions?: boolean) => void
) {
    apiClient
        .get(`/users/${id}`)
        .then((response: AxiosResponse<UserResponse>) => {
            if (response.data.result === "success") {
                callback(200, response.data.user, response.data.canEdit, response.data.hasAdminPermissions);
            } else {
                callback(500, null, false, false);
            }
        })
        .catch((error: AxiosError) => {
            console.error("Error fetching user:", error);
            callback(error.response?.status || 500, null, false, false);
        });
}

// Create a new user
export function CreateUser(
    userData: UserCreateRequest,
    callback: (statusCode: number, user: User | null) => void
) {
    apiClient
        .post("/users", userData)
        .then((response: AxiosResponse) => {
            if (response.data.result === "success") {
                callback(201, response.data.user);
            } else {
                callback(500, null);
            }
        })
        .catch((error: AxiosError) => {
            console.error("Error creating user:", error);
            callback(error.response?.status || 500, null);
        });
}

// Update an existing user
export function UpdateUser(
    id: number,
    userData: UserUpdateRequest,
    callback: (statusCode: number, user: User | null) => void
) {
    apiClient
        .put(`/users/${id}`, userData)
        .then((response: AxiosResponse) => {
            if (response.data.result === "success") {
                callback(200, response.data.user);
            } else {
                callback(500, null);
            }
        })
        .catch((error: AxiosError) => {
            console.error("Error updating user:", error);
            callback(error.response?.status || 500, null);
        });
}

// Get user audit history
export function GetUserHistory(
    userId: number,
    callback: (statusCode: number, history: UserHistory[]) => void
) {
    apiClient
        .get(`/users/${userId}/history`)
        .then((response: AxiosResponse<UserHistoryResponse>) => {
            if (response.data.result === "success") {
                callback(200, response.data.history);
            } else {
                callback(500, []);
            }
        })
        .catch((error: AxiosError) => {
            console.error("Error fetching user history:", error);
            callback(error.response?.status || 500, []);
        });
}

// Search for users
export function SearchUsers(
    searchData: UserSearchRequest,
    callback: (statusCode: number, users: User[]) => void
) {
    apiClient
        .post("/users/search", searchData)
        .then((response: AxiosResponse) => {
            if (response.data.result === "success") {
                callback(200, response.data.users);
            } else {
                callback(500, []);
            }
        })
        .catch((error: AxiosError) => {
            console.error("Error searching users:", error);
            callback(error.response?.status || 500, []);
        });
}

export interface LostAlumnus {
    id: number;
    first_name: string;
    last_name: string;
    year: number;
    pledge_class_id: number;
    pledge_class: PledgeClass;
    missing_email: boolean;
    missing_address: boolean;
}

export interface LostAlumniResponse {
    result: string;
    lost_alumni: LostAlumnus[];
}

// Get the org-wide list of lost alumni (graduated, not deceased/exiled,
// missing an email and/or mailing address)
export function GetLostAlumni(
    callback: (statusCode: number, lostAlumni: LostAlumnus[]) => void
) {
    apiClient
        .get("/users/lost-alumni")
        .then((response: AxiosResponse<LostAlumniResponse>) => {
            if (response.data.result === "success") {
                callback(200, response.data.lost_alumni);
            } else {
                callback(500, []);
            }
        })
        .catch((error: AxiosError) => {
            console.error("Error fetching lost alumni:", error);
            callback(error.response?.status || 500, []);
        });
}
